/* global SWDLocalise */
import { gsap } from 'gsap';
import { handleResponse, getClosest } from '../../utils';

let forms;

const getForms = () => document.querySelectorAll('.js-form');

const resetForm = (index) => {
    forms[index].inputs.forEach((input) => {
        switch (input.type) {
            case 'text':
            case 'textarea':
            case 'email':
            case 'select-one':
                input.value = '';
                break;

            case 'checkbox':
                input.checked = false;
                break;

            // no default
        }
    });

    forms[index].radioInputs.forEach((input) => {
        input.checked = false;
    });
};

const disableForm = (index) => {
    gsap.to(forms[index].formInner, {
        duration: 0.5,
        pointerEvents: 'none',
    });
    forms[index].loader.style.display = 'flex';
};

const enableForm = (index) => {
    gsap.to(forms[index].formInner, {
        duration: 0.5,
        pointerEvents: 'auto',
    });
    forms[index].loader.style.display = 'none';
};

const notice = (index, message, reset, type = 'error') => {
    let formatMessage = message;

    if (!Array.isArray(formatMessage)) {
        formatMessage = [formatMessage];
    }

    formatMessage = formatMessage.filter((item, idx) => formatMessage.indexOf(item) === idx);

    forms[index].notice.innerHTML = `
        <ul class="p-4 mt-6 mb-0 rounded-lg text-white list-none ${type === 'error' ? 'bg-error' : 'bg-success'}">${formatMessage.map((line) => `<li class="mt-1.5 first:mt-0 text-sm">${line}</li>`).join('')}</ul>
    `;

    if (reset) resetForm(index);

    enableForm(index);

    // reset accordion height to reveal notice
    const parent = getClosest(forms[index], '.js-collapsible-content');
    if (parent) parent.style.height = 'auto';
};

const clickHandle = (event, index, form) => {
    event.preventDefault();

    disableForm(index);

    const errors = [];
    const formData = [];
    let send = true;
    let email = '';
    let crmSignUp = false;
    let crmSignUpTag = '';

    forms[index].inputs.forEach((input) => {
        input.parentNode.classList.remove('has-error');
        forms[index].formConsent.parentNode.classList.remove('has-error');

        const required = input.classList.contains('is-required');
        if (required) {
            switch (input.type) {
                case 'text':
                case 'textarea':
                case 'email':
                case 'hidden':
                case 'select-one':
                    if (!input.value || input.value === 0) {
                        send = false;
                        input.parentNode.classList.add('has-error');
                    }
                    break;

                case 'checkbox':
                    if (input.checked === false) {
                        send = false;
                        input.parentNode.classList.add('has-error');
                    }
                    break;

                // no default
            }

            if (!send) {
                errors.push('Please complete the fields marked in red');
            }
        }

        // get primary email address
        if (input.type === 'email' && input.classList.contains('js-form-email')) {
            email = input.value;

            // check email is confirmed
            const emailConfirm = form.querySelector('.js-form-email-confirm');
            if (emailConfirm && email !== emailConfirm.value) {
                send = false;
                input.parentNode.classList.add('has-error');
                errors.push('Please check your email addresses match');
            }
        }

        // check if form has a GDPR consent field
        if (input.type === 'checkbox'
            && input.classList.contains('js-form-gdpr-consent')
            && !input.checked) {
            return;
        }

        // check if form has a CRM sign up field
        if (input.type === 'checkbox'
            && input.classList.contains('js-form-crm-sign-up')
            && input.checked) {
            crmSignUp = true;
            crmSignUpTag = input.dataset.tag;
        }

        formData.push({
            name: input.getAttribute('name'),
            value: input.value,
            required: required || false,
            label: input.dataset.label,
        });
    });

    // data consent
    if (!forms[index].formConsent.checked) {
        send = false;
        forms[index].formConsent.parentNode.classList.add('has-error');
        errors.push('Please tick the consent checkbox');
    }

    if (!email) {
        send = false;
        errors.push('A primary email address is required');
    }

    if (!send) {
        notice(index, errors);
    } else {
        const data = new FormData();
        data.append('action', form.dataset.action);
        data.append('nonce', form.dataset.nonce);
        data.append('email', email);
        data.append('form_data', JSON.stringify(formData));

        // check for CRM sign up form tag
        if (form.dataset.tag !== undefined) {
            data.append('tag', form.dataset.tag);
        }

        // check for CRM sign up field
        if (crmSignUp) {
            data.append('crm_sign_up', true);

            // check for CRM sign up field tag
            if (crmSignUpTag !== '') {
                data.append('crm_sign_up_tag', crmSignUpTag);
            }
        }

        // success download
        if (form.dataset.successDownload !== undefined) {
            data.append('success_download', form.dataset.successDownload);
        }

        fetch(SWDLocalise.ajaxUrl, {
            method: 'POST',
            cache: 'no-cache',
            body: data,
        })
            .then(handleResponse)
            .then((response) => {
                if (response.success) {
                    notice(index, (response.message.length > 0 ? response.message : form.dataset.successMessage), true, 'success');

                    // handle success download
                    if (response.download) {
                        forms[index].formInner.innerHTML = `
                            <div class="mb-8">
                                <p class="mb-6">Please click on the link below to view the brochure.</p>
                                <a
                                    href="${response.download.url}"
                                    class="group flex items-center w-min h-14 px-6 border-2 rounded-lg font-semibold select-none !no-underline text-center touch-manipulation whitespace-nowrap transition-colors border-lime bg-lime hover:bg-green hover:border-green hover:text-white mt-auto">
                                    <span>${response.download.label}</span>
                                </a>
                            </div>
                        `;
                    }
                } else {
                    notice(index, response.message);
                }
            })
            .catch((error) => {
                console.error(error); // eslint-disable-line no-console
            });
    }
};

const radioClickHandle = (input) => {
    if (input.checked) {
        const hiddenInput = input.parentNode.parentNode.querySelector('input[type=hidden]');
        hiddenInput.value = input.value;
    }
};

const init = () => {
    forms = getForms();
    if (!forms) return;

    forms.forEach((form, i) => {
        forms[i].formInner = form.querySelector('.js-form-inner');
        forms[i].notice = form.querySelector('.js-form-notice');
        forms[i].formConsent = form.querySelector('.js-form-consent');
        forms[i].inputs = form.querySelectorAll('.js-form-input');
        forms[i].radioInputs = form.querySelectorAll('.js-form-radio');
        forms[i].btn = form.querySelector('.js-form-btn');
        forms[i].loader = form.querySelector('.js-form-loader');

        if (!forms[i].btn) return;

        // prevent multiple events firing
        const newClickHandle = (event) => {
            clickHandle(event, i, form);
        };
        form.btn.addEventListener('click', newClickHandle, false);

        if (forms[i].radioInputs.length > 0) {
            forms[i].radioInputs.forEach((input) => {
                // prevent multiple events firing
                const newRadioClickHandle = () => {
                    radioClickHandle(input);
                };

                input.addEventListener('click', newRadioClickHandle, false);
            });
        }
    });
};

export default init;
